import type { WizardAsset } from '@/features/dashboard/components/watchlist-wizard/asset-grouping'
import { AssetClass } from '@/types/generated/common/types'

// How many assets of one class the list shows before it hands over to
// "See all". Small on purpose: the point of the section is to prove the
// class *has* results, not to enumerate them.
export const SECTION_CAP = 3

export interface AssetSectionDecoration {
  /** Rendered above this asset's row — the class this section opens. */
  header?: { assetClass: AssetClass | null; count: number }
  /** Rendered below this asset's row — jumps to the full class listing. */
  seeAll?: { assetClass: AssetClass }
}

export interface AssetSections<T extends WizardAsset> {
  assets: T[]
  decorations: Record<string, AssetSectionDecoration>
  /** False when the list is a plain uncapped list with no section chrome. */
  sectioned: boolean
}

// The class an asset belongs to — `asset.assetClass` (precomputed once in
// groupByAsset) normalized to `null` for "not classified yet", matching this
// module's convention for the trailing/unclassified bucket below.
export function effectiveAssetClass(asset: WizardAsset): AssetClass | null {
  return asset.assetClass ?? null
}

/**
 * Caps each asset class to its first `cap` assets and hangs the section
 * header / "See all" affordance off the first and last surviving row.
 *
 * Two things this deliberately does *not* do:
 *
 * - It never emits header rows. Decorations are keyed to an asset and drawn
 *   inside that asset's row block, so arrow-key navigation walks BTC → ETH →
 *   SOL and can never land on a non-selectable header.
 * - It never caps when fewer than two *classified* classes are present. A
 *   header that always reads the same thing is noise, and — more importantly
 *   for our catalog, which is nearly all crypto — capping a lone class to
 *   three rows would hide the entire list behind a "See all" link. This is
 *   also what makes "See all" self-dissolving: it sets the class filter,
 *   which leaves one class standing, which trips this early return.
 *
 * Unclassified assets are never capped (there is no class filter that could
 * reveal the rest of them) and only get a header once sectioning is on.
 */
export function buildAssetSections<T extends WizardAsset>(
  assets: T[],
  cap: number = SECTION_CAP
): AssetSections<T> {
  const byClass = new Map<AssetClass, T[]>()
  const unclassified: T[] = []

  for (const asset of assets) {
    const assetClass = effectiveAssetClass(asset)
    if (assetClass === null) {
      unclassified.push(asset)
      continue
    }
    const bucket = byClass.get(assetClass)
    if (bucket) bucket.push(asset)
    else byClass.set(assetClass, [asset])
  }

  if (byClass.size < 2) return { assets, decorations: {}, sectioned: false }

  const out: T[] = []
  const decorations: Record<string, AssetSectionDecoration> = {}

  const decorate = (key: string, decoration: AssetSectionDecoration) => {
    decorations[key] = { ...decorations[key], ...decoration }
  }

  // Section order is first appearance in the already-ranked list, not a fixed
  // array — relevance decides which class leads, not taste.
  // Keyed by `.id` (unique group identity), not `.key` (display key).
  for (const [assetClass, bucket] of byClass) {
    const shown = bucket.slice(0, cap)
    decorate(shown[0].id, { header: { assetClass, count: bucket.length } })
    if (bucket.length > cap) decorate(shown[shown.length - 1].id, { seeAll: { assetClass } })
    out.push(...shown)
  }

  if (unclassified.length > 0) {
    decorate(unclassified[0].id, { header: { assetClass: null, count: unclassified.length } })
    out.push(...unclassified)
  }

  return { assets: out, decorations, sectioned: true }
}
